import { useContext, useEffect, useState } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { ToolbarPlugin, toolbarPlugin, ToolbarSlot } from '@react-pdf-viewer/toolbar';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import { Box, Divider } from '@mui/material';
import PrimarySkeleton from '../loading/PrimarySkeleton';
import { BodyText, CaptionText } from '../typography/PrimaryTypography';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import pdfjsPackage from 'pdfjs-dist/package.json';
import axios, { AxiosResponse } from 'axios';
import { CLOUD_FUNCTION_URL } from '../../../env';
import { getAuthHeaderForUser, handleAuthError } from '../../../lib/auth/auth.util';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';

const SOURCE = 'PdfViewer.tsx';

const PDF_WORKER_URL = `https://unpkg.com/pdfjs-dist@${pdfjsPackage.version}/build/pdf.worker.min.js`;

type PdfViewerErrorType = 'server' | 'render';

const pdf_viewer_error_messages: Record<PdfViewerErrorType, { heading: string; action: string }> = {
  server: {
    heading: 'This document could not be loaded.',
    action:
      'There may be an issue on our end. Please try again later or contact us if the problem persists.'
  },
  render: {
    heading: 'Your browser had trouble displaying this document.',
    action: 'Try opening in a different browser. If the problem persists, please contact us.'
  }
};

type PdfViewerProps =
  | { pdf_url: string; storage_bucket?: never; storage_file_path?: never }
  | { pdf_url?: never; storage_bucket: string; storage_file_path: string };

/**
 * Inline PDF viewer. Renders pages as canvas elements via PDF.js without native browser PDF UI.
 * @param {string | undefined} pdf_url Full public URL to the PDF file.
 * @param {string | undefined} storage_bucket GCS bucket containing the private file.
 * @param {string | undefined} storage_file_path Object path within the bucket, used to fetch a signed URL via Cloud Function.
 * @returns {JSX.Element}
 */
const PdfViewer = ({ pdf_url, storage_bucket, storage_file_path }: PdfViewerProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [pdfUrl, setPdfUrl] = useState<string | null>(pdf_url ?? null);
  const [error, setError] = useState<PdfViewerErrorType | null>(null);

  const pdf_toolbar_plugin: ToolbarPlugin = toolbarPlugin();
  const { Toolbar } = pdf_toolbar_plugin;

  // Either set the pdf url from direct parent prop or use signed url for bucket and storage path.
  useEffect(() => {
    if (!storage_file_path) return;
    (async () => {
      try {
        const response: AxiosResponse = await axios.post(
          `${CLOUD_FUNCTION_URL}/getSignedFileUrls`,
          { bucket: storage_bucket, path: storage_file_path },
          { headers: { ...(await getAuthHeaderForUser()) } }
        );
        setPdfUrl(response.data.data.urls[0].url);
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getSignedFileUrls'),
            { storage_file_path, error_message: err instanceof Error ? err.message : String(err) },
            tracking_id
          )
        );
        setFeedbackData({
          message: FEEDBACK_ACTION_FAILURE('Unable to load document', tracking_id),
          state: true,
          type: 'error'
        });
        setError('server');
        handleAuthError(err);
      }
    })();
  }, [storage_bucket, storage_file_path]);

  if (error !== null) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 800,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <BodyText>{pdf_viewer_error_messages[error].heading}</BodyText>
        <CaptionText sx={{ maxWidth: 400, textAlign: 'center' }}>
          {pdf_viewer_error_messages[error].action}
        </CaptionText>
      </Box>
    );
  }

  if (!pdfUrl) {
    return <PrimarySkeleton width="100%" height={800} variant="rounded" />;
  }

  return (
    <Box
      onContextMenu={(e) => e.preventDefault()}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
    >
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 1,
          py: 0.5,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Toolbar>
          {({
            CurrentPageInput,
            GoToNextPage,
            GoToPreviousPage,
            NumberOfPages,
            Zoom,
            ZoomIn,
            ZoomOut
          }: ToolbarSlot) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <GoToPreviousPage />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CurrentPageInput />
                <CaptionText>
                  / <NumberOfPages />
                </CaptionText>
              </Box>
              <GoToNextPage />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <ZoomOut />
              <Zoom />
              <ZoomIn />
            </Box>
          )}
        </Toolbar>
      </Box>
      {/* Fixed A4-proportioned viewport */}
      <Box sx={{ height: 800, overflow: 'auto' }}>
        <Worker workerUrl={PDF_WORKER_URL}>
          <Viewer
            fileUrl={pdfUrl}
            plugins={[pdf_toolbar_plugin]}
            renderLoader={() => <PrimarySkeleton width="100%" height={800} variant="rounded" />}
            renderError={() => {
              setError('render');
              return <></>;
            }}
          />
        </Worker>
      </Box>
    </Box>
  );
};

export default PdfViewer;
